import { Sun, Moon, MonitorSmartphone, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDisplaySettings, type ThemeMode } from "@/components/DisplaySettings";
import { useEffect, useState } from "react";

/**
 * Day / Night / Follow Device picker.
 * Reads from and writes to the existing DisplaySettings provider so the choice
 * applies app-wide and persists across sessions.
 */
export function ThemeToggle({
  triggerClassName,
  align = "end",
}: {
  triggerClassName?: string;
  align?: "start" | "end" | "center";
}) {
  const { prefs, setPrefs } = useDisplaySettings();
  const theme = prefs.theme;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark =
    mounted && (theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches));

  const options: { value: ThemeMode; label: string; emoji: string; Icon: typeof Sun }[] = [
    { value: "light", label: "Day Mode", emoji: "☀️", Icon: Sun },
    { value: "dark", label: "Night Mode", emoji: "🌙", Icon: Moon },
    { value: "system", label: "Follow Device", emoji: "🌓", Icon: MonitorSmartphone },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Appearance"
        className={
          triggerClassName ||
          "inline-flex h-[44px] w-[44px] items-center justify-center rounded-full border border-gold/40 bg-gold-light/30 text-[var(--gold-deep)] transition-colors hover:bg-[var(--gradient-gold)] hover:text-foreground hover:shadow-[var(--shadow-gold)]"
        }
      >
        {isDark ? (
          <Moon className="h-4 w-4" strokeWidth={2.25} />
        ) : (
          <Sun className="h-4 w-4" strokeWidth={2.25} />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} className="w-52">
        <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Appearance
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => {
          const active = theme === opt.value;
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={(e) => {
                e.preventDefault();
                setPrefs({ theme: opt.value });
              }}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2">
                <span aria-hidden>{opt.emoji}</span>
                <span>{opt.label}</span>
              </span>
              {active && <Check className="h-3.5 w-3.5 text-[var(--gold-deep)]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
